import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ShadeMode, SyfoAppState, VaultDeletedInfo, VaultState } from './state';
import { VaultSettingsNode } from '@syfo/types/vault.types';
import { readShadeThunk } from './thunks/read-shade-thunk';
import { saveShadeThunk } from './thunks/save-shade-thunk';
import { listVaultsThunk } from './thunks/list-vaults-thunk';
import { readVaultThunk } from './thunks/read-vault-thunk';
import { saveVaultThunk } from './thunks/save-vault-thunk';
import { uploadVaultThunk } from './thunks/upload-vault-thunk';
import { deleteVaultThunk } from './thunks/delete-vault-thunk';
import { setVaultSyncEnabledThunk } from './thunks/set-vault-sync-enabled-thunk';
import { registerVaultThunk } from './thunks/register-vault-thunk';
import { checkAuthThunk } from './thunks/check-auth-thunk';
import { updateProfileNameThunk } from './thunks/update-profile-thunk';
import * as documentReducers from './reducers/document-reducers';
import * as expenditureReducers from './reducers/expenditure-reducers';
import * as incomeReducers from './reducers/income-reducers';
import * as itemReducers from './reducers/item-reducers';
import * as liabilityReducers from './reducers/liability-reducers';
import * as personReducers from './reducers/person-reducers';
import * as petReducers from './reducers/pet-reducers';
import * as propertyReducers from './reducers/property-reducers';
import * as protectionReducers from './reducers/protection-reducers';
import * as savingsReducers from './reducers/savings-reducers';
import * as vehicleReducers from './reducers/vehicle-reducers';

const initialState: SyfoAppState = {
  mode: ShadeMode.Light,
  currentVault: null,
  vaults: [],
  auth: {
    isAuthenticated: null, // null = not checked yet
    userProfile: null,
    sessionExpired: false
  },
  deleted: null,
  isLoadingVaults: true
};

// Create the slice
const vaultSlice = createSlice({
  name: 'vault',
  initialState,
  reducers: {

    // Set the current vault
    setCurrentVault: (state, action: PayloadAction<VaultState>) => {
      state.currentVault = action.payload;
    },
    
    // Clear the current vault
    clearCurrentVault: (state) => {
      state.currentVault = null;
    },

    // Create a new vault
    createVault: (state, action: PayloadAction<{ vault: VaultState; setAsCurrent?: boolean }>) => {
      const { vault, setAsCurrent = false } = action.payload;
      
      // Add to vaults list if not already there
      if (!state.vaults.find(v => v.info.id === vault.info.id)) {
        state.vaults.push({ 
          info: vault.info, 
          syncState: vault.syncState,
          isSyncEnabled: false
        });
      }
      
      // Set as current vault if requested
      if (setAsCurrent) {
        state.currentVault = vault;
      }
    },

    // Set specific mode
    setMode: (state, action: PayloadAction<ShadeMode>) => {
      state.mode = action.payload;
    },

    // Update vault info (name, etc.)
    updateVaultInfo: (state, action: PayloadAction<{ vaultId: string; name: string }>) => {
      const { vaultId, name } = action.payload;
      
      // Update in current vault if it matches
      if (state.currentVault && state.currentVault.info.id === vaultId) {
        state.currentVault.info.name = name;
        state.currentVault.syncState.lastChangeDate = new Date().toISOString();
      }
      
      // Update in vaults list
      const vault = state.vaults.find(v => v.info.id === vaultId);
      if (vault) {
        vault.info.name = name;
        vault.syncState.lastChangeDate = new Date().toISOString();
      }
    },

    // Update vault units (currency and distance)
    updateVaultUnits: (state, action: PayloadAction<{ currency: string; odometer: string }>) => {
      if (state.currentVault) {
        state.currentVault.data.units = action.payload;
        state.currentVault.syncState.lastChangeDate = new Date().toISOString();
      }
    },

    // Update vault feature settings
    updateVaultSettings: (state, action: PayloadAction<VaultSettingsNode>) => {
      if (state.currentVault) {
        state.currentVault.data.settings = action.payload;
        state.currentVault.syncState.lastChangeDate = new Date().toISOString();
      }
    },

    // Update sync status based on online/offline state
    vaultDeleted: (state, action: PayloadAction<VaultDeletedInfo>) => {
      state.deleted = action.payload;
    },

    clearVaultDeleted: (state) => {
      state.deleted = null;
    },

    sessionExpired: (state) => {
      state.auth.sessionExpired = true;
      state.auth.isAuthenticated = false;
      state.auth.userProfile = null;
    },

    ...documentReducers,
    ...expenditureReducers,
    ...incomeReducers,
    ...itemReducers,
    ...liabilityReducers,
    ...personReducers,
    ...petReducers,
    ...propertyReducers,
    ...protectionReducers,
    ...savingsReducers,
    ...vehicleReducers
  },
  extraReducers: (builder) => {
    builder
      // Fetch vault cases
      .addCase(readVaultThunk.pending, () => {
        // Loading state could be added here
      })
      .addCase(readVaultThunk.fulfilled, (state, action) => {
        state.currentVault = action.payload;
        
        // Initialize expenditures array if it doesn't exist (migration for older vaults)
        if (!state.currentVault.data.expenditures) {
          state.currentVault.data.expenditures = [];
        }
        
        // Add to vaults list if not already there
        if (!state.vaults.find(v => v.info.id === action.payload.info.id)) {
          state.vaults.push({ 
            info: action.payload.info, 
            syncState: action.payload.syncState,
            isSyncEnabled: false
          });
        }
      })
      .addCase(readVaultThunk.rejected, () => {
        // Error handling could be added here
      })
      
      // Save vault cases
      .addCase(saveVaultThunk.pending, () => {
        // Loading state could be added here
      })
      .addCase(saveVaultThunk.fulfilled, (state, action) => {
        // Update in vaults list
        state.vaults = state.vaults.map(v =>
          v.info.id === action.payload.info.id
            ? { ...v, info: action.payload.info, syncState: action.payload.syncState }
            : v
        );
      })
      .addCase(saveVaultThunk.rejected, () => {
        // Error handling could be added here
      })
      
      // Load mode cases
      .addCase(readShadeThunk.fulfilled, (state, action) => {
        state.mode = action.payload;
      })
      
      // Save mode cases (no state update needed, just persistence)
      .addCase(saveShadeThunk.fulfilled, () => {
        // Mode saving successful, no state change needed
      })
      
      // Fetch vault list cases
      .addCase(listVaultsThunk.pending, (state) => {
        state.isLoadingVaults = true;
      })
      .addCase(listVaultsThunk.fulfilled, (state, action) => {
        state.vaults = action.payload;
        state.isLoadingVaults = false;
      })
      .addCase(listVaultsThunk.rejected, (state) => {
        state.isLoadingVaults = false;
      })
      
      // Delete vault cases
      .addCase(deleteVaultThunk.pending, () => {
        // Loading state could be added here
      })
      .addCase(deleteVaultThunk.fulfilled, (state, action) => {
        const deletedVaultId = action.payload;
        
        // Remove vault from vaults list
        state.vaults = state.vaults.filter(v => v.info.id !== deletedVaultId);
        
        // Clear current vault if it was the deleted one
        if (state.currentVault && state.currentVault.info.id === deletedVaultId) {
          state.currentVault = null;
        }
      })
      .addCase(deleteVaultThunk.rejected, () => {
        // Error handling could be added here
      })
      
      // Sync enabled state cases
      .addCase(uploadVaultThunk.pending, () => {
        // Upload in progress — machine state tracks this
      })
      .addCase(uploadVaultThunk.fulfilled, (state, action) => {
        const { lastModified, vaultId } = action.payload;
        
        // Update current vault's serverLastModified if available
        if (state.currentVault && lastModified) {
          state.currentVault.syncState.serverLastModified = lastModified;
        }
        
        // Update vault syncState in the list
        const uploadedVault = state.vaults.find(v => v.info.id === vaultId);
        if (uploadedVault) {
          uploadedVault.syncState.lastSyncDate = new Date().toISOString();
          if (lastModified) {
            uploadedVault.syncState.serverLastModified = lastModified;
          }
        }
      })
      .addCase(uploadVaultThunk.rejected, () => {
        // Upload failed — machine state handles error/pause tracking
      })
      
      // Set vault sync enabled cases
      .addCase(setVaultSyncEnabledThunk.fulfilled, (state, action) => {
        const { vaultId, isSyncEnabled } = action.payload;
        const syncVault = state.vaults.find(v => v.info.id === vaultId);
        if (syncVault) {
          syncVault.isSyncEnabled = isSyncEnabled;
        }
      })
      
      // Register vault cases
      .addCase(registerVaultThunk.pending, () => {
        // Registration in progress — machine state tracks this
      })
      .addCase(registerVaultThunk.fulfilled, (state, action) => {
        const { vaultId, syncId, lastModified } = action.payload;
        const registeredVault = state.vaults.find(v => v.info.id === vaultId);
        if (registeredVault) {
          // Store syncId (server-assigned ID) - client vaultId stays the same
          registeredVault.syncState.syncId = syncId;
          registeredVault.syncState.serverLastModified = lastModified;
          registeredVault.isSyncEnabled = true;
          registeredVault.isOwned = true;
        }
        
        // Also update currentVault if it's the one being registered
        if (state.currentVault && state.currentVault.info.id === vaultId) {
          state.currentVault.syncState.syncId = syncId;
          state.currentVault.syncState.serverLastModified = lastModified;
        }
      })
      .addCase(registerVaultThunk.rejected, () => {
        // Registration failed — machine state handles error tracking
      })

      // Authentication check cases
      .addCase(checkAuthThunk.pending, () => {
        // Keep current auth state while checking
      })
      .addCase(checkAuthThunk.fulfilled, (state, action) => {
        if (action.payload) {
          state.auth.isAuthenticated = true;
          state.auth.userProfile = action.payload;
        } else {
          state.auth.isAuthenticated = false;
          state.auth.userProfile = null;
        }
      })
      .addCase(checkAuthThunk.rejected, (state) => {
        state.auth.isAuthenticated = false;
        state.auth.userProfile = null;
      })

      // Update profile name cases
      .addCase(updateProfileNameThunk.pending, () => {
        // Keep current state while updating
      })
      .addCase(updateProfileNameThunk.fulfilled, (state, action) => {
        // Update the userProfile with the new name
        if (state.auth.userProfile) {
          state.auth.userProfile = action.payload;
        }
      })
      .addCase(updateProfileNameThunk.rejected, (_, action) => {
        console.error('[VaultSlice] Failed to update profile name:', action.payload);
      })
  },
});

// Export actions
export const {
  setCurrentVault,
  clearCurrentVault,
  createVault,
  setMode,
  updateVaultInfo,
  updateVaultUnits,
  updateVaultSettings,
  vaultDeleted,
  clearVaultDeleted,
  sessionExpired,

  addDocument,
  updateDocument,
  removeDocument,
  
  addIncome,
  updateIncome,
  removeIncome,
  
  addExpenditure,
  updateExpenditure,
  removeExpenditure,
  
  addItem,
  updateItem,
  removeItem,
  
  addLiability,
  updateLiability,
  removeLiability,

  addPerson,
  updatePerson,
  removePerson,

  addPet,
  updatePet,
  removePet,
  
  addProperty,
  updateProperty,
  removeProperty,

  addProtection,
  updateProtection,
  removeProtection,

  addSavings,
  updateSavings,
  removeSavings,
  
  addVehicle,
  updateVehicle,
  removeVehicle
} = vaultSlice.actions;

// Export thunks
export { deleteVaultThunk, saveVaultThunk, setVaultSyncEnabledThunk };

// Export reducer
export default vaultSlice.reducer;